import { IComponentOptions, IController, IPromise } from "angular";
import { StateService } from "@uirouter/angularjs";
import { firstValueFrom, take } from "rxjs";
import { BadgeService, IExtendedBadgeAssignment } from "@gtmhub/badges";
import { GtmhubController } from "@gtmhub/core";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { CurrentEmployeeActions, ICurrentEmployeeStoreState, IEmployee } from "@gtmhub/employees";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { NewHomeExperienceService } from "@webapp/home/services/new-home-experience.service";
import { TaskModalService } from "@webapp/tasks/services/task-modal.service";

const bindStateToCtrl = (state: ICurrentEmployeeStoreState): IHomeFeedNavBarCtrlRedux => {
  const { info: currentEmployee, isFetched: currentEmployeeFetched, error: currentEmployeeError } = state.currentEmployee;

  return {
    reduxStatesInitialized: currentEmployeeFetched,
    reduxStateError: currentEmployeeError,
    employee: currentEmployee,
  };
};

interface IHomeFeedNavBarCtrlRedux {
  employee: IEmployee;
  reduxStatesInitialized: boolean;
  reduxStateError: IUIError;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface HomeFeedNavBarCtrl extends IHomeFeedNavBarCtrlRedux {}
export class HomeFeedNavBarCtrl extends GtmhubController implements IController {
  public loading: boolean;
  public badgeAssignments: IExtendedBadgeAssignment[];
  public isMsTeamsState: boolean;
  public isNextTopNavBarEnabled: boolean;
  public hasPermission: (action: string) => boolean;
  public featureAvailable: (name: string) => boolean;
  private isA15CreateTaskFormEnabled: boolean;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$ngRedux",
    "UIErrorHandlingService",
    "CurrentEmployeeActions",
    "BadgeService",
    "ApmSpanService",
    "NewHomeExperienceService",
    "FeatureTogglesFacade",
    "TaskModalService",
  ];

  constructor(
    $rootScope: IGtmhubRootScopeService,
    private $scope: IExtendedRootScopeService,
    private $state: StateService,
    private $ngRedux: INgRedux,
    private uiErrorHandlingService: UIErrorHandlingService,
    private currentEmployeeActions: CurrentEmployeeActions,
    private badgeService: BadgeService,
    private apmSpanService: ApmSpanService,
    private newHomeExperienceService: NewHomeExperienceService,
    private featureToggleService: FeatureTogglesFacade,
    private taskModalService: TaskModalService
  ) {
    super();

    this.newHomeExperienceService
      .isNewNavigationEnabled$()
      .pipe(take(1))
      .subscribe((isFeatureAvailable) => {
        this.isNextTopNavBarEnabled = isFeatureAvailable;
      });

    firstValueFrom(this.featureToggleService.isFeatureAvailable$(FeatureFlag.A15CreateTaskForm)).then((enabled) => {
      this.isA15CreateTaskFormEnabled = enabled;
    });

    this.isMsTeamsState = $rootScope.isMsTeamsState;
    this.hasPermission = $rootScope.hasPermission.bind($rootScope);
    this.featureAvailable = $rootScope.featureAvailable.bind($rootScope);
    this.loading = true;
    this.onDestroy(this.$ngRedux.connect(bindStateToCtrl)(this));
  }

  $onInit(): void {
    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.reduxStatesInitialized,
        () => this.reduxStateError
      )
      .then(
        () => this.getCurrentUserBadgeAssignments(),
        () => this.handleError(this.reduxStateError)
      )
      .then(() => {
        this.loading = false;
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  private getCurrentUserBadgeAssignments = (): IPromise<unknown> => {
    return this.apmSpanService.dataLoadSpan("badges-load", () => {
      return this.badgeService.getCurrentUserBadgeAssignments().then((badgeAssignments) => {
        this.badgeAssignments = badgeAssignments;
      });
    });
  };

  private handleError = <E>(error: E): void => {
    this.uiErrorHandlingService.handleModal(error);
  };

  public openCreateTask = (): void => {
    if (!this.isA15CreateTaskFormEnabled) {
      const state = this.isMsTeamsState ? "gtmhub.msTeamsHome.dashboard._createTask.createTask" : "._createTask.createTask";
      this.$state.go(state);
      return;
    }

    this.taskModalService.openCreateTask();
  };
}

export const HomeFeedNavBarComponent: IComponentOptions = {
  bindings: {
    hideActionButtons: "<?",
    title: "<?",
  },
  controller: HomeFeedNavBarCtrl,
  template: require("./home-feed-nav-bar.component.html"),
};
